import { useState } from "react";
import { Play, Clock } from "lucide-react";
import BizzVideoModal from "./BizzVideoModal"; // Your modal component

interface VideoGridProps {
  videos: any[];
  showUser?: boolean;
}

const VideoGrid = ({ videos, showUser = true }: VideoGridProps) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  if (!videos || videos.length === 0) {
    return (
      <div className="flex-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-dark-4 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play className="h-8 w-8 text-gray-500" />
          </div>
          <p className="text-gray-400">No videos found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ul className="grid-container">
        {videos.map((video) => (
          <li key={video.$id} className="relative min-w-80 h-80">
            <div
              onClick={() => setSelectedVideoId(video.$id)}
              className="grid-post_link group cursor-pointer"
            >
              {/* Video Thumbnail */}
              <div className="relative w-full h-full bg-black rounded-lg overflow-hidden">
                
                  <video
                    src={video.videoUrl}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                  />
                
                {/* Play Icon Overlay */}
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/20 rounded-full p-4 backdrop-blur-sm">
                    <Play className="h-8 w-8 text-white fill-white" />
                  </div>
                </div>

                {/* Video Duration */}
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                  </div>
                )}

                {/* Category Badge */}
                <div className="absolute top-2 left-2 bg-primary-500/80 px-2 py-1 rounded text-xs text-white font-medium">
                  Bizz
                </div>

                {/* Stats Overlay */}
                {video.stats && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    {video.stats.likes > 0 && (
                      <div className="bg-black/70 px-1.5 py-0.5 rounded text-xs text-white flex items-center gap-1">
                        ❤️ {video.stats.likes}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <h3 className="text-white text-sm font-semibold line-clamp-2 mb-1">
                  {video.title}
                </h3>
                
                {showUser && video.creator && (
                  <div className="flex items-center gap-2">
                    <img
                      src={video.creator.imageUrl}
                      alt={video.creator.name}
                      className="w-5 h-5 rounded-full"
                    />
                    <span className="text-gray-300 text-xs">
                      @{video.creator.username}
                    </span>
                  </div>
                )}

                {/* Interaction Date (for saved/liked videos) */}
                {video.interactionDate && (
                  <div className="text-xs text-gray-400 mt-1">
                    {showUser ? 'Saved' : 'Liked'} • {new Date(video.interactionDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Video Modal */}
      {selectedVideoId && (
        <BizzVideoModal
          isOpen={!!selectedVideoId}
          onClose={() => setSelectedVideoId(null)}
          videoId={selectedVideoId}
        />
      )}
    </>
  );
};

export default VideoGrid;